import { useEffect, useState } from "react";
import { Search, Loader2, MapPin, Eye, Lock, Navigation } from "lucide-react";
import { discoverSearch } from "@/lib/api";
import { getTrustBadge, guessManufacturer } from "./dealFeedData";
import { TIER_CONFIG } from "@/lib/tierGate";
import { snagMapLocation } from "@/lib/outbound";

const CATEGORIES = [
  { id: "PHYSICAL", label: "Physical" },
  { id: "DIGITAL", label: "Digital" },
  { id: "MAP", label: "Map" },
];

// Maps the backend's internal verdict codes (VERDICT_LEGIT / VERDICT_OVERPRICED /
// VERDICT_HIGH_RISK) onto Section 2's shared badge renderer (VERIFIED_LEGIT / etc.)
// so the same 🟢🟡🔴 badge text is used everywhere.
function badgeFor(verdict) {
  const key = (verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", "");
  return getTrustBadge(key);
}

// DISCOVER viewport — Section 3. Search bar + 3 SOP category pills, conditional ZIP
// input for Map, structured result grid. Section 5 — results beyond the caller's tier
// `maxSourcesShown` (FREE=1, PLUS=3, PRO/ELITE=unlimited) are masked behind a single
// locked CTA tile instead of being fetched-and-hidden, matching the Scroll feed rule.
export default function DiscoverView({ active, currentTier = "FREE", onLocked, onOpenPdp, location }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("PHYSICAL");
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isSeedData, setIsSeedData] = useState(false);

  // Saved Location Check — a signed-in user's ZIP/State (grabbed automatically on
  // login) pre-fills the Map search so they never have to retype it every visit.
  useEffect(() => {
    if (category === "MAP" && !zip && location?.zip) setZip(location.zip);
  }, [category, zip, location]);

  const submit = async (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await discoverSearch({ query: query.trim(), category, location: { zip_code: category === "MAP" ? zip.trim() : "", radius_miles: 30 } });
      setResults(res.results || []);
      setSummary(res.search_summary || null);
      setIsSeedData(res.source === "seed");
      if (res.source === "seed") {
        // eslint-disable-next-line no-console
        console.warn("[Snaggedify Alpha] Live product search is unavailable right now — showing sample results for this search.");
      }
    } catch (err) {
      setError(err?.status === 422 && err?.message ? err.message : "Search is temporarily unavailable — please try again shortly.");
      setResults(null);
      setSummary(null);
      setIsSeedData(false);
    } finally {
      setLoading(false);
    }
  };

  const cfg = TIER_CONFIG[currentTier] || TIER_CONFIG.FREE;
  const maxShown = cfg.maxSourcesShown === Infinity ? (results?.length || 0) : cfg.maxSourcesShown;
  const visibleResults = results ? results.slice(0, maxShown) : [];
  const lockedCount = results ? Math.max(0, results.length - maxShown) : 0;
  const nextTier = currentTier === "FREE" ? "PLUS" : "PRO";

  return (
    <section id="view-discover" data-testid="view-discover" className={active ? "tab-view active" : "tab-view hidden"}>
      <form onSubmit={submit} className="flex flex-col gap-3" data-testid="discover-search-form">
        <div className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 dark:border-white/10 dark:bg-white/5">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a product, gas, or local listing…"
            data-testid="discover-search-input"
            className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-zinc-500"
          />
          <button type="submit" disabled={!query.trim() || loading} data-testid="discover-search-submit" className="shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-wide text-white disabled:opacity-40 dark:bg-white dark:text-slate-950">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </button>
        </div>

        <div className="flex items-center gap-2" data-testid="discover-category-pills">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              data-testid={`discover-category-${c.id.toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-wide ${category === c.id ? "bg-gradient-to-r from-brand-yellow to-brand-orange text-slate-950" : "border border-slate-300 text-slate-500 dark:border-white/10 dark:text-zinc-400"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {category === "MAP" && (
          <div className="flex flex-wrap items-center gap-2" data-testid="discover-map-location">
            <div className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 dark:border-white/10 dark:bg-white/5">
              <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="US ZIP Code (e.g. 60803)"
                data-testid="discover-zip-input"
                className="h-11 w-40 bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-zinc-500"
              />
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-slate-500 dark:bg-white/10 dark:text-zinc-400" data-testid="discover-radius-lock">
              Restricted to 30-Mile Radius (US)
            </span>
          </div>
        )}
      </form>

      {error && <p className="mt-3 text-sm font-bold text-rose-500" data-testid="discover-error">{error}</p>}

      {isSeedData && (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-amber-600 dark:text-amber-400" data-testid="discover-demo-data-banner">
          Demo Data — live results are temporarily unavailable, showing sample prices for this search
        </p>
      )}

      {summary?.message && (
        <p className="mt-3 text-sm font-bold text-amber-600 dark:text-amber-400" data-testid="discover-map-unavailable">{summary.message}</p>
      )}

      {summary?.msrp_baseline != null && (
        <p className="mt-3 text-xs font-bold text-slate-500 dark:text-zinc-400" data-testid="discover-msrp-baseline">MSRP baseline: ${summary.msrp_baseline.toFixed(2)}</p>
      )}

      {results && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="discover-results-grid">
          {results.length === 0 ? (
            <p className="col-span-full text-center text-sm font-bold text-slate-400 dark:text-zinc-500" data-testid="discover-no-results">No US-verified results found — try a different search.</p>
          ) : (
            <>
              {visibleResults.map((r) => {
                const badge = badgeFor(r.verdict);
                const pdpDeal = {
                  id: r.id, title: r.title, price: Number(r.price) || 0, msrp: r.msrp_baseline != null ? Number(r.msrp_baseline) : Number(r.price) || 0,
                  image: r.image || "", reseller: r.reseller, manufacturer: guessManufacturer(r.title || ""),
                  verdict: (r.verdict || "").replace("VERDICT_LEGIT", "VERIFIED_LEGIT").replace("VERDICT_", ""),
                  outboundUrl: r.outbound_url,
                  fulfillment: r.fulfillment_mode || (category === "MAP" ? "IN_STORE_PICKUP" : "ONLINE_SHIPPING"),
                  isMapResult: category === "MAP",
                  location: category === "MAP" ? { address: r.address, lat: r.lat, lng: r.lng } : undefined,
                };
                return (
                  <article key={r.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/5" data-testid={`discover-result-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-black uppercase tracking-wide ${badge.className}`}><badge.Icon className="h-3.5 w-3.5" strokeWidth={2.75} /> {badge.label}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[9px] font-black uppercase tracking-wide text-slate-500 dark:bg-white/10 dark:text-zinc-400" data-testid={`discover-result-fulfillment-${r.id}`}>
                        {category === "MAP" ? "In-Store Pickup" : "Online Shipping"}
                      </span>
                    </div>
                    <h4 className="mt-2 line-clamp-2 cursor-pointer text-xs font-bold text-slate-900 dark:text-white" onClick={() => onOpenPdp?.(pdpDeal)} data-testid={`discover-result-open-pdp-${r.id}`}>{r.title}</h4>
                    <p className="mt-1 text-sm font-black text-brand-orange dark:text-amber-400">
                      {r.price != null ? `$${Number(r.price).toFixed(2)}` : "Price unavailable"}
                      {r.msrp_baseline != null && <span className="ml-1.5 text-[10px] font-bold text-slate-400 line-through dark:text-zinc-500">MSRP ${Number(r.msrp_baseline).toFixed(2)}</span>}
                    </p>
                    <p className="mt-1 truncate text-[10px] font-bold text-slate-500 dark:text-zinc-400">
                      {r.reseller} {r.distance_miles != null && `· ${r.distance_miles} mi`}
                    </p>
                    <button
                      onClick={() => onOpenPdp?.(pdpDeal)}
                      data-testid={`discover-result-review-${r.id}`}
                      className="mt-2 flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-2 text-[11px] font-black uppercase text-white dark:bg-white dark:text-slate-950"
                    >
                      <Eye className="h-3.5 w-3.5" /> Review Product
                    </button>
                    {category === "MAP" && (
                      <button
                        onClick={() => snagMapLocation({ title: r.title, address: r.address, lat: r.lat, lng: r.lng, itemId: r.id })}
                        data-testid={`discover-result-directions-${r.id}`}
                        className="mt-1.5 flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 py-2 text-[11px] font-black uppercase text-slate-600 dark:border-white/10 dark:text-zinc-300"
                      >
                        <Navigation className="h-3.5 w-3.5" /> Get Directions
                      </button>
                    )}
                  </article>
                );
              })}
              {lockedCount > 0 && (
                <button
                  onClick={() => onLocked?.(nextTier)}
                  data-testid="discover-results-locked"
                  className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3 text-center dark:border-white/10 dark:bg-white/5"
                >
                  <Lock className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                  <p className="text-[10px] font-black uppercase leading-tight text-slate-500 dark:text-zinc-400">
                    Unlock {lockedCount}+ more result{lockedCount === 1 ? "" : "s"} with {nextTier} (${TIER_CONFIG[nextTier].priceMonthly.toFixed(2)})
                  </p>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
